<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition">
    <Transition
      enter-active-class="duration-1000 page-enter-transition"
      leave-active-class="duration-300 page-leave-transition"
      enter-from-class="page-enter-from"
      leave-to-class="page-enter-from"
    >
      <div v-if="!isLoading" class="sectionContainer">
        <div class="slider fixed h-screen w-screen bg-orange z-10" />
        <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
          <!-- content begin -->
          <div
            v-if="!vacancyData"
            class="col-start-2 col-span-12 flex items-center justify-center py-10 min-h-[50vh]"
          >
            <div class="spinner" />
          </div>
          <div
            v-else
            class="col-start-2 col-span-12 mt-[100px] lg:mt-[140px] md:col-span-12 md:col-start-2"
          >
            <paragraph
              v-if="pageProperties?.title"
              :text="pageProperties?.title"
              :is-umbraco="isUmbraco"
              :theme="themeType"
              classes="font-bold mb-4 lg:mb-8"
            />
            <header1
              :text="vacancyData.name"
              classes="mb-8 lg:mb-[94px]"
              :is-umbraco="isUmbraco"
              :theme="themeType"
            />

            <div
              class="grid grid-cols-12 gap-x-5 md:gap-x-8 lg:gap-x-12 2xl:gap-x-20 col-span-12"
            >
              <div class="col-span-full lg:col-span-3">
                <link-button
                  :label="ctaLabel"
                  :url="applicationUrl"
                  :is-umbraco="isUmbraco"
                  classes="mb-12 lg:mb-20"
                  :theme="themeType"
                />

                <vacancy-label
                  v-if="vacancyData.custReqType != 'Evergreen'"
                  :label="applicationLabel"
                  :textArray="[vacancyData.postEndDateString]"
                  :is-umbraco="isUmbraco"
                  :themeType="themeType"
                  classes="mb-7"
                />
                <vacancy-label
                  :label="recruitmentType"
                  :textArray="[vacancyData.jobtype]"
                  :is-umbraco="isUmbraco"
                  :themeType="themeType"
                  classes="mb-7"
                />
                <vacancy-label
                  :label="countryLabel"
                  :textArray="[vacancyData.countries.join(', ')]"
                  :is-umbraco="isUmbraco"
                  :themeType="themeType"
                  classes="mb-7"
                />

                <vacancy-label
                  :label="location"
                  :textArray="[vacancyData.locations.join(', ')]"
                  :is-umbraco="isUmbraco"
                  :themeType="themeType"
                  classes="mb-7"
                />
                <vacancy-label
                  :label="reqID"
                  :textArray="[vacancyData.reqId]"
                  :is-umbraco="isUmbraco"
                  :themeType="themeType"
                  classes="mb-7"
                />
              </div>
              <div class="col-start-1 col-span-full lg:col-span-6 lg:col-start-4">
                <rich-text
                  v-if="vacancyData.description"
                  :text="strippedContent"
                  :isUmbraco="isUmbraco"
                  :theme="themeType"
                  classes="[&_span]:font-normal col-span-full"
                />
              </div>
              <div class="col-start-1 col-span-full lg:col-span-6 lg:col-start-4">
                <social-module
                  :url="currentUrl ?? ''"
                  :label="shareLabel.valueOf()"
                  :icons="[...iconArray]"
                  vacancypage
                />
              </div>
            </div>
          </div>
          <!-- content end -->
        </page-wrapper>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import {
  changeTheme,
  formatDate,
  getThemeType,
  getParentFromUrl,
  StripHtml,
} from "@/util/helper";
import { getVacancyPage, getTranslations, getVacancyById } from "@/util/api";
import { Theme } from "@/types/types";
import Header1 from "@components/Typography/Header1.ce.vue";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import VacancyLabel from "@atoms/VacancyLabel/VacancyLabel.vue";
import SocialModule from "@molecules/Socials/Socials.ce.vue";
import { IVacancyPage } from "@/types/IVacancyPage";
import { IVacancy } from "@/types/IVacancy";
import PageWrapper from "@components/PageWrapper.vue";
import { getPathForPageData } from "@/util/getPathForPageData";
import RichText from "@components/Typography/RichText.ce.vue";
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import { getVacancyTags } from "@/util/api";

const isLoading = ref(true);
const isUmbraco = ref(false);
const pageGUI = ref<string | undefined>();
const pageData = ref<IVacancyPage | null>(null);
const vacancyData = ref<IVacancy | null>(null);
const theme = ref<Theme | null>();
const shareLabel = ref<string>("");
const locationLabel = ref<string>("");
const locationsLabel = ref<string>("");
const applicationLabel = ref<string>("");
const applicationCountryLabel = ref<string>("");
const applicationCountriesLabel = ref<string>("");
const reqID = ref<string>("");
const recruitmentType = ref<string>("");
const ctaLabel = ref<string>("");
const iconArray = ref<Array<{ icon: string }>>([]);
const currentUrl = ref<string | null>(null);
const pageKey = ref<string | undefined>();

currentUrl.value = window.location.href;
iconArray.value = [
  { icon: "Facebook" },
  { icon: "Twitter" },
  { icon: "LinkedIn" },
  { icon: "MailTo" },
];

const getVacancyId = () => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get("id");
};

const fetchInitialData = async () => {
  // Fetch page and translation data
  pageKey.value = document.documentElement.dataset.id;
  const pageParams = getPathForPageData();
  if (!pageParams?.path || !pageKey.value) return;

  [
    pageData.value,
    shareLabel.value,
    locationLabel.value,
    locationsLabel.value,
    applicationLabel.value,
    reqID.value,
    recruitmentType.value,
    ctaLabel.value,
    applicationCountryLabel.value,
    applicationCountriesLabel.value,
  ] = await Promise.all([
    getVacancyPage(pageKey.value, pageParams?.isPreview) as Promise<IVacancyPage>,
    getTranslations("ShareTitle"),
    getTranslations("VacancyLocationLabel"),
    getTranslations("VacancyLocationsLabel"),
    getTranslations("ApplicationDeadline"),
    getTranslations("ReqID"),
    getTranslations("RecruitmentType"),
    getTranslations("Apply"),
    getTranslations("VacancyCountry"),
    getTranslations("VacancyCountries"),
  ]);
  await handlePageData();
  await loadVacancyData();
};

const handlePageData = async () => {
  if (!pageData.value) return;
  pageGUI.value = pageData.value.route.startItem.id;
  updateTheme();
  isLoading.value = false;
};

const loadVacancyData = async () => {
  const vacancyId = getVacancyId();
  if (!vacancyId) {
    window.location.href = `${parentUrl.value}`;
  }
  if (!vacancyId) return;
  vacancyData.value = await getVacancyById(vacancyId);

  if (!vacancyData.value || !vacancyData.value?.reqId) {
    window.location.href = `${parentUrl.value}`;
  }
  if (vacancyData.value && vacancyData.value.reqId) {
    await UpdateMetaTags();
  }
};

const updateTheme = () => {
  if (pageProperties.value) {
    const backgroundColor = pageProperties.value.backgroundColor;
    theme.value = backgroundColor ? backgroundColor : theme.value;
  }
  if (theme.value) changeTheme(theme.value);
};

const UpdateMetaTags = async () => {
  if (vacancyData.value) {
    let vacancyType = await getVacancyType(vacancyData.value.custReqType);
    let passClosingDateBool = await passClosingDate(
      vacancyData.value.postEndDate.toString()
    );
    let hasContent = vacancyData.value.description.length > 0 ? true : false;
    //OBS Locations are joined by ; rather that , in the meta tags. This is to avoid conflicts with the , in the locations array in Ankiro
    document.title = vacancyData.value.name;
    const metaTags = [
      { name: "jobcategory", content: vacancyData.value.category },
      { name: "locations", content: vacancyData.value.locations.join(";") },
      { name: "country", content: vacancyData.value.country },
      { name: "countries", content: vacancyData.value.countries.join(",") },
      { name: "postingStatus", content: vacancyData.value.postingStatus },
      { name: "closingDateString", content: vacancyData.value.postEndDateString },
      { name: "closingDate", content: vacancyData.value.postEndDate.toString() },
      { name: "publishDate", content: vacancyData.value.publishDate.toString() },
      { name: "company", content: vacancyData.value.company },
      { name: "reqId", content: vacancyData.value.reqId },
      { name: "custReqType", content: vacancyData.value.custReqType },
      { name: "vacancyType", content: vacancyType },
      { name: "passClosingDate", content: passClosingDateBool.toString() },
    ];
    metaTags.forEach((tag) => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", tag.name);
      meta.setAttribute("content", tag?.content ? tag.content : "");
      document.head.appendChild(meta);
    });
  }
};

const passClosingDate = async (closingDate: string) => {
  const today = new Date();
  const closing = new Date(closingDate);
  return today > closing;
};

const getVacancyType = async (type: string) => {
  if (type === "Evergreen") {
    return "Evergreen";
  }

  const data = await getVacancyTags();
  const tags = data.items.map((tag) => {
    return `${tag.properties.title.toLowerCase()}`;
  });
  return tags.includes(type.toLowerCase()) ? "Student" : "Global";
};

onBeforeMount(fetchInitialData);

const pageProperties = computed(() => {
  return pageData.value?.properties;
});

const themeType = computed(() => {
  if (!theme.value) return "light";
  return getThemeType(theme.value);
});

const parentUrl = computed(() => {
  if (!pageData.value?.route.path) return;
  return getParentFromUrl(pageData.value.route.path);
});

const countryLabel = computed(() => {
  if (!vacancyData.value?.countries) return applicationCountryLabel.value;
  let countries: string[] = vacancyData.value?.countries;
  let number: number = countries.length;
  if (number > 1) {
    return applicationCountriesLabel.value;
  } else {
    return applicationCountryLabel.value;
  }
});

const location = computed(() => {
  if (!vacancyData.value?.locations) return locationLabel.value;
  let locationCounter: number = vacancyData.value?.locations.length;
  if (locationCounter > 1) {
    return locationsLabel.value;
  } else {
    return locationLabel.value;
  }
});

const strippedContent = computed(() => {
  if (!vacancyData.value?.description) return "";
  return StripHtml(vacancyData.value.description);
});

const applicationUrl = computed(() => {
  return `https://career55.sapsf.eu/career?career_ns=job_listing&company=cowiholdinP2&career_job_req_id=${vacancyData.value?.reqId}`;
});
</script>
